import '@/styles/globals.css'
// riskintel.css is page-scoped (.riskintel-page) and used only on
// /products/riskintel, so it is loaded per-page there (public/styles) instead
// of globally — keeps ~30 kB of CSS off every other page's critical path.
// ai-first.css is likewise loaded per-page (public/styles) by AiFirstStaticSSR
// and ServicePage, the only consumers of its classes.
// aifx.css is page-scoped (.aifx) and used only on the ai-first-engineering
// pages, so it is loaded per-page via AiFirstStaticSSR (public/styles) instead
// of globally — keeps ~27 kB of CSS off every other page's critical path.
import '@/styles/hero-home.css'
import '@/styles/services-page.css'
// Scoped to /services/managed-services (page-specific class names only).
import '@/styles/managed-services.css'
import '@/Components/hero-home/BorderGlow.css'
import '@/Components/hero-home/Particles.css'
import localFont from '@next/font/local'
import { SessionProvider } from 'next-auth/react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import dynamic from 'next/dynamic'
import React, { startTransition, useState, useEffect } from 'react'
import Navbar from '@/Components/Navbar'

const CHUNK_RELOAD_KEY = 'chunkReloadAt'
const CHUNK_RELOAD_WINDOW_MS = 30000

const isChunkLoadError = (error) =>
    error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '')

// A chunk that fails to load is almost always a tab opened before a deploy (its
// chunks no longer exist) or a request that hit the restart window. A reload
// fetches the current build. At most once per CHUNK_RELOAD_WINDOW_MS, so a chunk
// that is really missing shows the fallback instead of reloading forever.
const reloadOnceForChunkError = () => {
    try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0
        if (Date.now() - last < CHUNK_RELOAD_WINDOW_MS) return false
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    } catch (_) {
        return false
    }
    window.location.reload()
    return true
}

// Global Error Boundary to catch client-side exceptions gracefully
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    componentDidCatch(error, errorInfo) {
        console.error('[ErrorBoundary] Caught client-side error:', error, errorInfo)
        if (isChunkLoadError(error)) reloadOnceForChunkError()
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '2rem', textAlign: 'center' }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>Something went wrong</h2>
                    <p style={{ color: '#666', marginBottom: '1.5rem' }}>We apologize for the inconvenience. Please try refreshing the page.</p>
                    <button
                        onClick={() => {
                            this.setState({ hasError: false })
                            if (typeof window !== 'undefined') window.location.reload()
                        }}
                        style={{ padding: '0.75rem 2rem', backgroundColor: '#058BF0', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '1rem' }}>
                        Refresh Page
                    </button>
                </div>
            )
        }
        return this.props.children
    }
}

// Server-rendered on purpose: the footer is the site's main internal-link hub, and with
// ssr: false none of its links existed in the HTML crawlers receive. It only uses
// useState and an analytics effect, so it is safe to render on the server.
const Footer = dynamic(() => import('../Components/Footer'))

import DynamicHead from '@/utils/DynamicHead'
import { LOGO_URL } from '@/lib/products'
import { safeReturnPath } from '@/utils/thankYou'
import { ParallaxProvider } from 'react-scroll-parallax'

// For optional client-only widgets: if the chunk fails to load, render nothing
// instead of throwing. A failed import otherwise throws during render — inside
// the ErrorBoundary it replaces the whole page with the fallback, and outside it
// (VisitorBeacon) Next shows "Application error: a client-side exception".
const optionalWidget = (load) => () => load().catch(() => () => null)

// Interaction-triggered, below-the-fold widgets — keep them out of the initial
// _app bundle so they don't delay first render / inflate Total Blocking Time.
const BackToTop = dynamic(optionalWidget(() => import('@/Components/BackToTop')), { ssr: false })
const ExitIntentPopup = dynamic(optionalWidget(() => import('@/Components/ExitIntentPopup')), { ssr: false })

// First-party visitor capture. Client-only and renders nothing, so it never touches
// the server render path or the initial bundle's critical work. Rendered outside
// the ErrorBoundary, so it must never throw (see optionalWidget).
const VisitorBeacon = dynamic(optionalWidget(() => import('@/Components/VisitorBeacon')), { ssr: false })

// Fonts are self-hosted (latin subsets from Google Fonts, in styles/fonts) so
// `next build` never has to reach fonts.googleapis.com — the CI/Docker build
// failed whenever that fetch did.
const inter = localFont({
    src: '../styles/fonts/Inter-latin-var.woff2',
    weight: '100 900',
    display: 'swap',
    variable: '--font-inter',
})
// display:'swap' paints text immediately in the fallback then swaps to Poppins,
// so the font never blocks first paint of the hero <h1> (the LCP element).
// Weight 500 dropped (barely used with Poppins); 400/600/700/800 cover the rest.
const poppins = localFont({
    src: [
        { path: '../styles/fonts/Poppins-400.woff2', weight: '400', style: 'normal' },
        { path: '../styles/fonts/Poppins-600.woff2', weight: '600', style: 'normal' },
        { path: '../styles/fonts/Poppins-700.woff2', weight: '700', style: 'normal' },
        { path: '../styles/fonts/Poppins-800.woff2', weight: '800', style: 'normal' },
    ],
    display: 'swap',
    // Not preloaded: the four Poppins files were competing with the stylesheet
    // for bandwidth on mobile and delaying first paint. With swap the headings
    // paint in the size-adjusted fallback first; Inter (body text) stays
    // preloaded.
    preload: false,
    variable: '--font-poppins',
})

const isProd = process.env.NODE_ENV === 'production'

// How long after the load event trackers wait for an interaction before
// loading anyway (see loadFallbackDue in App).
const TRACKER_FALLBACK_MS = 5000

const whenIdle = (cb) =>
    'requestIdleCallback' in window ? requestIdleCallback(cb, { timeout: 1000 }) : setTimeout(cb, 1)
const cancelIdle = (id) =>
    'cancelIdleCallback' in window ? cancelIdleCallback(id) : clearTimeout(id)

// Reddit Ads Manager pixel. Public ID (it ships in page HTML, like the Meta Pixel
// ID below); NEXT_PUBLIC_REDDIT_PIXEL_ID overrides it, e.g. for a test pixel.
const REDDIT_PIXEL_ID = process.env.NEXT_PUBLIC_REDDIT_PIXEL_ID || 'a2_jwnp58tdb86u'

// The Apollo client moved to @/lib/gqlClient — no component uses Apollo hooks,
// so it no longer needs to be created or provided app-wide from here. This keeps
// @apollo/client off the shared bundle (notably the homepage).

export default function App({ Component, pageProps }) {
    const router = useRouter()

    // Both flags start as null ("unknown") and are resolved after mount. Reading
    // localStorage / navigator in a useState initializer made the first client
    // render differ from the server render (e.g. the Meta Pixel <noscript> was
    // dropped for internal visitors), which threw React hydration errors
    // #418/#423 on every page. Trackers only mount once a flag is known to be
    // false, so internal traffic is still never tracked.
    const [isInternalTraffic, setIsInternalTraffic] = useState(null)
    const [isInAppBrowser, setIsInAppBrowser] = useState(null)

    useEffect(() => {
        let internal = false
        try {
            internal = localStorage.getItem('is_internal') === 'true'
        } catch (_) {}
        setIsInternalTraffic(internal)
        setIsInAppBrowser(/FBAN|FBAV|FB_IAB|FB4A|Instagram/i.test(navigator.userAgent || ''))
    }, [])

    const trackingAllowed = isProd && isInternalTraffic === false

    // n8n chat and the third-party trackers (GA/GTM, HubSpot, Meta, Clarity,
    // Leadfeeder, Factors, SessionRewind) load after the visitor's first
    // interaction instead of at page load: together they were most of the
    // page's Total Blocking Time. Reddit stays at page load (see below).
    //
    // 'wheel', not 'scroll': the browser fires scroll on its own when it
    // restores the position on reload/back or jumps to a #anchor. The state
    // change waits for an idle moment so the injected scripts don't compete
    // with the tap that triggered them (INP).
    // These updates are wrapped in startTransition: they can fire while the
    // page's lazy (dynamic) components are still hydrating, and a plain
    // setState there makes React drop their server HTML and re-render them on
    // the client ("Suspense boundary received an update before it finished
    // hydrating").
    const [interacted, setInteracted] = useState(false)
    useEffect(() => {
        const events = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'mousemove']
        let idleId
        const want = () => {
            events.forEach((e) => window.removeEventListener(e, want))
            idleId = whenIdle(() => startTransition(() => setInteracted(true)))
        }
        events.forEach((e) => window.addEventListener(e, want, { passive: true }))
        return () => {
            events.forEach((e) => window.removeEventListener(e, want))
            cancelIdle(idleId)
        }
    }, [])

    // Visitors who read without interacting (e.g. an ad click that bounces)
    // must still be counted, so trackers also load TRACKER_FALLBACK_MS after
    // the page's load event — late enough that Lighthouse has finished
    // measuring Total Blocking Time.
    const [loadFallbackDue, setLoadFallbackDue] = useState(false)
    useEffect(() => {
        let timer
        let idleId
        const schedule = () => {
            timer = setTimeout(() => {
                idleId = whenIdle(() => startTransition(() => setLoadFallbackDue(true)))
            }, TRACKER_FALLBACK_MS)
        }
        if (document.readyState === 'complete') schedule()
        else window.addEventListener('load', schedule, { once: true })
        return () => {
            window.removeEventListener('load', schedule)
            clearTimeout(timer)
            cancelIdle(idleId)
        }
    }, [])
    const trackersAllowed = trackingAllowed && (interacted || loadFallbackDue)

    // Turnstile api.js stays on every page load: several forms poll for it for
    // only a few seconds after mount. Pages listed here instead request it (the
    // 'cf-turnstile:load' event, or the flag if it fired before this listener)
    // once their form nears the viewport — see UpgradeCheckForm.
    // On '/' the forms are the bottom ContactPage (mounted near the viewport)
    // and the hero / exit-intent popups (contactus.js); both request on mount.
    const lazyTurnstilePages = ['/', '/services/managed-services']
    const [turnstileRequested, setTurnstileRequested] = useState(false)
    useEffect(() => {
        if (window.__cfTurnstileWanted) startTransition(() => setTurnstileRequested(true))
        const want = () => startTransition(() => setTurnstileRequested(true))
        window.addEventListener('cf-turnstile:load', want)
        return () => window.removeEventListener('cf-turnstile:load', want)
    }, [])
    const loadTurnstile = turnstileRequested || !lazyTurnstilePages.includes(router.pathname)

    useEffect(() => {
        if (!trackingAllowed) return
        if (typeof window === 'undefined') return

        const firePageview = (url) => {
            const params = new URLSearchParams(window.location.search)

            window.gtag('event', 'custom_pageview', {
                send_to:        process.env.NEXT_PUBLIC_GA_ID,
                page_url:       url,
                page_referrer:  document.referrer,
                utm_source:     params.get('utm_source')   || '(direct)',
                utm_medium:     params.get('utm_medium')   || '(none)',
                utm_campaign:   params.get('utm_campaign') || '(none)',
                utm_term:       params.get('utm_term')     || '',
                utm_content:    params.get('utm_content')  || '',

                timestamp:      Date.now(),
                screen_width:   window.screen.width,
                screen_height:  window.screen.height,
                device_type:    window.innerWidth <= 768   ? 'mobile'
                              : window.innerWidth <= 1024  ? 'tablet' : 'desktop',
                user_agent:     navigator.userAgent,
                language:       navigator.language,
            })
        }

        // window.gtag is the queue stub from <Head> until gtag.js loads, so
        // pageviews (and route changes before it loads) are queued, not lost.
        if (window.gtag) firePageview(window.location.href)

        const handleRouteChange = (url) => {
            if (window.gtag) firePageview(url)
            if (window.rdt) window.rdt('track', 'PageVisit')
        }

        router.events.on('routeChangeComplete', handleRouteChange)
        return () => router.events.off('routeChangeComplete', handleRouteChange)
    }, [router.events, trackingAllowed])

    const isWebAppLandingPage = router.pathname === '/custom-web-development-services'
    const isMobileApplanding = router.pathname === '/custom-web-application-development-services'
    const devopsPages = ['/devops', '/cloud', '/soc2', '/pci-dss', '/devops-staff-augmentation', '/iso-27001']
    // /thankyou reached from one of those landing pages keeps the same bare layout
    const thankYouFrom = router.pathname === '/thankyou' ? safeReturnPath(router.query.from) : null
    const isDevopsPage = devopsPages.includes(router.pathname) || devopsPages.includes(thankYouFrom)

    // Define pages that should use ParallaxProvider
    const pagesWithParallax = ['/products/integra-ai']
    const shouldUseParallax = pagesWithParallax.includes(router.pathname)

    // Rendered as a plain <script> inside <Head> below so it is in the server HTML.
    // It used to go through next/script, whose default strategy injects after hydration,
    // so no crawler ever saw it. Add the G2 and Upwork profile URLs to sameAs once known.
    let schema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/#organization`,
        name: 'Cloudastra Technologies',
        legalName: 'Cloudastra Technologies',
        url: process.env.NEXT_PUBLIC_NEXTAUTH_URL,
        logo: LOGO_URL,
        description:
            'Software agency building enterprise B2B products and AI, cloud and DevOps solutions: DebtPulse, SecureFlow, RiskIntel, Revinu, MuleNet, iPreferAir, Integra-AI, Nigmabot AI, MedAI and Aquaflowz.',
        sameAs: [
            'https://in.linkedin.com/company/cloudastra-technologies',
            'https://github.com/Cloudastra-Tech',
            'https://www.facebook.com/profile.php?id=100095608861845&mibextid=ZbWKwL',
            'https://www.youtube.com/channel/UCZ7tKHqBwnVRJo_9WYJ5_-A',
        ],
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'sales',
            url: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/contact`,
        },
    }

    return (
    
            <main className={`${inter.className} ${poppins.variable}`}>
            <DynamicHead aiFirstMeta={pageProps.aiFirstMeta} />
            {/* n8n chat CSS is loaded non-blocking (preload + swap) in
                _document.js. The render-blocking copy that used to sit here was
                removed so it no longer stalls first paint. */}
            <Head>
                <script
                    dangerouslySetInnerHTML={{
                        __html: `
            (function() {
                const trackers = ['googletagmanager', 'google-analytics', 'facebook.net', 'lfeeder', 'sessionrewind', 'clarity.ms', 'hs-scripts', 'hscollectedforms', 'reb2b', 'doubleclick', 'facebook.com', 'redditstatic'];
                const trackerRejectionMessages = ['Object Not Found Matching Id:'];

                // Improved error handler with logging
                const originalOnError = window.onerror;
                window.onerror = function(message, source, lineno, colno, error) {
                    const isTrackerError = source && trackers.some(t => source.includes(t));

                    if (isTrackerError) {
                        console.warn('[Tracker Error Suppressed]', {
                            message: message,
                            source: source,
                            line: lineno,
                            column: colno
                        });
                        return true;
                    }

                    console.error('[Application Error]', {
                        message: message,
                        source: source,
                        line: lineno,
                        column: colno,
                        error: error
                    });

                    if (originalOnError) {
                        return originalOnError(message, source, lineno, colno, error);
                    }
                    return false;
                };

                // Resource loading error handler
                window.addEventListener('error', function(event) {
                    if (event.target && (event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK' || event.target.tagName === 'IMG')) {
                        const resourceUrl = event.target.src || event.target.href || '';
                        const isTrackerResource = trackers.some(t => resourceUrl.includes(t));

                        if (isTrackerResource) {
                            console.warn('[Tracker Resource Failed - Suppressed]', {
                                tag: event.target.tagName,
                                url: resourceUrl
                            });
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                        }

                        console.warn('[Resource Loading Error]', {
                            tag: event.target.tagName,
                            url: resourceUrl
                        });

                        event.preventDefault();
                        return false;
                    }
                }, true);

                // Promise rejection handler
                window.addEventListener('unhandledrejection', function(event) {
                    const reason = event.reason;
                    const stack = reason && reason.stack;
                    const reasonStr = typeof reason === 'string' ? reason : (reason && reason.message) || '';
                    const isTrackerError =
                        (stack && trackers.some(t => stack.includes(t))) ||
                        (reasonStr && (
                            trackers.some(t => reasonStr.includes(t)) ||
                            trackerRejectionMessages.some(m => reasonStr.includes(m))
                        ));

                    if (isTrackerError) {
                        console.warn('[Tracker Promise Rejection Suppressed]', {
                            reason: reason
                        });
                        event.preventDefault();
                    } else {
                        console.error('[Unhandled Promise Rejection]', {
                            reason: reason
                        });
                    }
                });

                // Service Worker error handler
                if ('serviceWorker' in navigator) {
                    navigator.serviceWorker.addEventListener('error', function(error) {
                        console.warn('[Service Worker Error]', error);
                    });
                }
            })();
            `,
                    }}
                />
                {/* gtag queue stub. gtag.js itself loads late (see trackersAllowed),
                    so window.gtag exists from first paint and every call made
                    before then (pageviews, form events, the /thank conversion)
                    sits in dataLayer until gtag.js/GTM load and send it. For
                    internal traffic nothing ever loads, so nothing is sent. */}
                {isProd && process.env.NEXT_PUBLIC_GA_ID && (
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                if (!window.gtag) {
                    window.dataLayer = window.dataLayer || [];
                    window.gtag = function(){dataLayer.push(arguments);};
                    gtag('js', new Date());
                    gtag('config', '${process.env.NEXT_PUBLIC_GA_ID}', {
                        send_page_view: true,
                        cookie_flags: 'SameSite=None;Secure'
                    });
                }
            `,
                        }}
                    />
                )}
                <script
                    key='organization-schema'
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
                />
            </Head>

            {/* Google Analytics (its js/config commands are queued by the stub in <Head>) */}
            {trackersAllowed && process.env.NEXT_PUBLIC_GA_ID && (
                <Script
                    id='gtag-base'
                    src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GA_ID}`}
                    strategy='afterInteractive'
                    onError={(e) => {
                        console.warn('[Google Analytics Script] Failed to load:', e)
                    }}
                />
            )}

            {/* SessionRewind - Keep as lazyOnload */}
            {trackersAllowed && process.env.NEXT_PUBLIC_SESSION_REWIND && (
                <Script
                    id='sessionrewind'
                    strategy='lazyOnload'
                    dangerouslySetInnerHTML={{
                        __html: `
                    !function (o) {
                        var w = window;
                        w.SessionRewindConfig = o;
                        var f = document.createElement("script");
                        f.async = 1, f.crossOrigin = "anonymous",
                        f.src = "https://rec.sessionrewind.com/srloader.js";
                        f.onerror = function() {
                            console.warn('[SessionRewind] Failed to load');
                        };
                        var g = document.getElementsByTagName("head")[0];
                        g.insertBefore(f, g.firstChild);
                    }({
                        apiKey: '${process.env.NEXT_PUBLIC_SESSION_REWIND}',
                        startRecording: true
                    });
                    `,
                    }}
                />
            )}

            {/* Google Tag Manager */}
            {trackersAllowed && process.env.NEXT_PUBLIC_GTM_ID && (
                <Script
                    id='gtm-script'
                    strategy='afterInteractive'
                    dangerouslySetInnerHTML={{
                        __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;
            j.onerror=function(){console.warn('[GTM] Failed to load');};
            f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID}');
            `,
                    }}
                />
            )}

            {/* Leadfeeder Tracking Script */}
            {/* Skip in Facebook/Instagram in-app browsers — their injected content script
                wraps document.createEvent with a wrong `this`, causing LeadFeeder to throw
                "Illegal invocation". IAB sessions are sandboxed so LeadFeeder data is unreliable there anyway. */}
            {trackersAllowed && isInAppBrowser === false && (
                <Script
                    id='leadfeeder-tracker'
                    strategy='lazyOnload'
                    onError={(e) => {
                        console.warn('[Leadfeeder Script] Failed to load:', e)
                    }}
                    dangerouslySetInnerHTML={{
                        __html: `
                    (function(ss,ex){
                        window.ldfdr=window.ldfdr||function(){(ldfdr._q=ldfdr._q||[]).push([].slice.call(arguments));};
                        (function(d,s){
                            fs=d.getElementsByTagName(s)[0];
                            function ce(src){
                                var cs=d.createElement(s);
                                cs.src=src;
                                cs.async=1;
                                cs.onerror=function(){console.warn('[Leadfeeder] Failed to load');};
                                fs.parentNode.insertBefore(cs,fs);
                            };
                            ce('https://sc.lfeeder.com/lftracker_v1_'+ss+(ex?'_'+ex:'')+'.js');
                        })(document,'script');
                    })('DzLR5a5NypD8BoQ2');
                    `,
                    }}
                />
            )}

            {/* Meta Pixel Code  */}
            {trackersAllowed && (
                <>
                    <Script
                        id='facebook-pixel'
                        strategy='lazyOnload'
                        onError={(e) => {
                            console.warn('[Meta Pixel Script] Failed to load:', e)
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `
                            !function(f,b,e,v,n,t,s)
                            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                            n.queue=[];t=b.createElement(e);t.async=!0;
                            t.src=v;t.onerror=function(){console.warn('[FB Pixel] Failed to load');};
                            s=b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t,s)}(window, document,'script',
                            'https://connect.facebook.net/en_US/fbevents.js');
                            fbq('init', '1174411864211096');
                            fbq('track', 'PageView');
                        `,
                        }}
                    />
                </>
            )}
            {/* Reddit Pixel: attributes ad clicks and leads (the Lead event is sent by
                lead forms, e.g. the managed-services upgrade check). Production
                only, never internal traffic, but unlike the trackers above it does
                not wait for trackersAllowed: it loads afterInteractive, so visitors
                who bounce quickly from an ad are still counted. */}
            {trackingAllowed && REDDIT_PIXEL_ID && (
                <Script
                    id='reddit-pixel'
                    strategy='afterInteractive'
                    dangerouslySetInnerHTML={{
                        __html: `
                        !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?
                        p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];
                        var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js";
                        t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
                        rdt('init', '${REDDIT_PIXEL_ID}');
                        rdt('track', 'PageVisit');
                    `,
                    }}
                />
            )}
            {/* Kept in the server HTML (isInternalTraffic is null there and on the
                first client render) so no-JS visitors still get the pixel. */}
            {isProd && isInternalTraffic !== true && (
                <noscript>
                    <img
                        height='1'
                        width='1'
                        style={{ display: 'none' }}
                        src='https://www.facebook.com/tr?id=1174411864211096&ev=PageView&noscript=1'
                        alt=''
                    />
                </noscript>
            )}

            {trackersAllowed && (
                <Script
                    id='hubspot-chat-script'
                    type='text/javascript'
                    strategy='lazyOnload'
                    defer
                    src='//js-na2.hs-scripts.com/243694114.js'
                    onError={(e) => {
                        console.warn('[HubSpot Script] Failed to load:', e)
                    }}
                />
            )}

            {/* Cloudflare Turnstile Script */}
            {loadTurnstile && (
                <Script
                    id='cloudflare-turnstile'
                    src='https://challenges.cloudflare.com/turnstile/v0/api.js'
                    strategy='afterInteractive'
                    onError={(e) => {
                        console.warn('[Cloudflare Turnstile] Failed to load:', e)
                    }}
                />
            )}

            {/* n8n Chat Widget Script (after first interaction, see interacted) */}
           {!isDevopsPage && interacted && (
                <Script
                    id='n8n-chat-script'
                    type='module'
                    strategy='lazyOnload'
                    dangerouslySetInnerHTML={{
                        __html: `
                        try {
                            if (!window.__n8nChatInitialized) {
                                const { createChat } = await import('https://cdn.jsdelivr.net/npm/@n8n/chat@0.6.2/dist/chat.bundle.es.js');
                                createChat({
                                    webhookUrl: '/api/n8n-chat',
                                    initialMessages: ['Hi there! 👋', 'I am Astra Bot, How can I assist you today?']
                                });
                                window.__n8nChatInitialized = true;
                            }
                        } catch (e) {
                            console.warn('[n8n Chat] Failed to initialize:', e);
                        }
                        `,
                    }}
                />
            )}

            {/* Hide n8n chat widget on devops pages */}
            {isDevopsPage && (
                <style jsx global>{`
                    .chat-window-wrapper,
                    [class*='chat-window'],
                    [id*='chat-window'],
                    [class*='n8n-chat'],
                    [id*='n8n-chat'] {
                        display: none !important;
                    }
                `}</style>
            )}

            {/* Microsoft Clarity */}
            {trackersAllowed && process.env.NEXT_PUBLIC_CLARITY_ID && (
                <Script id='microsoft-clarity' strategy='afterInteractive'>
                    {`
                (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    t.onerror=function(){console.warn('[Clarity] Failed to load');};
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                })(window, document, "clarity", "script", "${process.env.NEXT_PUBLIC_CLARITY_ID}");
                `}
                </Script>
            )}

            {/* Factors.ai Tracking Script */}
            {trackersAllowed && (
                <Script
                    id='factors-ai'
                    strategy='lazyOnload'
                    onError={(e) => {
                        console.warn('[Factors.ai Script] Failed to load:', e)
                    }}
                    dangerouslySetInnerHTML={{
                        __html: `
                        window.faitracker=window.faitracker||function(){this.q=[];var t=new CustomEvent("FAITRACKER_QUEUED_EVENT");return this.init=function(t,e,a){this.TOKEN=t,this.INIT_PARAMS=e,this.INIT_CALLBACK=a,window.dispatchEvent(new CustomEvent("FAITRACKER_INIT_EVENT"))},this.call=function(){var e={k:"",a:[]};if(arguments&&arguments.length>=1){for(var a=1;a<arguments.length;a++)e.a.push(arguments[a]);e.k=arguments[0]}this.q.push(e),window.dispatchEvent(t)},this.message=function(){window.addEventListener("message",function(t){"faitracker"===t.data.origin&&this.call("message",t.data.type,t.data.message)})},this.message(),this.init("${process.env.NEXT_PUBLIC_FACTORS_TOKEN}",{host:"https://api.factors.ai"}),this}(),function(){var t=document.createElement("script");t.type="text/javascript",t.src="https://app.factors.ai/assets/factors.js",t.async=!0,(d=document.getElementsByTagName("script")[0]).parentNode.insertBefore(t,d)}();
                        `,
                    }}
                />
            )}

            {/* First-party visitor → company capture. Production only, never
                internal traffic, and not deferred like the third-party trackers
                (it is small); the server applies its own filters again on ingest. */}
            {trackingAllowed && <VisitorBeacon />}

            <PageTree
                Component={Component}
                pageProps={pageProps}
                pathname={router.pathname}
                shouldUseParallax={shouldUseParallax}
                hideNavbar={isWebAppLandingPage || isMobileApplanding || isDevopsPage}
                isDevopsPage={isDevopsPage}
            />
            </main>
    )
}

// Memoized so App's own state (the tracker / Turnstile / internal-traffic flags
// above) doesn't re-render the whole page. Those flags flip after load or on the
// first tap, and each flip used to re-render every section of the page — long
// main-thread tasks right when the visitor starts interacting.
const PageTree = React.memo(function PageTree({
    Component,
    pageProps,
    pathname,
    shouldUseParallax,
    hideNavbar,
    isDevopsPage,
}) {
    return (
            <ErrorBoundary>
            {shouldUseParallax ?
                <ParallaxProvider>
                    {hideNavbar ? null : <Navbar />}

                    <SessionProvider session={pageProps.session}>
                        <Component {...pageProps} />
                    </SessionProvider>
                    {pathname === '/devops-solutions' ? null : <Footer />}
                    <BackToTop />
                    {isDevopsPage ? null : <ExitIntentPopup />}
                </ParallaxProvider>
            :   <>
                    {hideNavbar ? null : <Navbar />}

                    <SessionProvider session={pageProps.session}>
                        <Component {...pageProps} />
                    </SessionProvider>
                    {isDevopsPage ? null : <Footer />}
                    <BackToTop />
                    {isDevopsPage ? null : <ExitIntentPopup />}
                </>
            }
            </ErrorBoundary>
    )
})